import { FunctionComponent, useState, ReactNode } from "react";
import styled, { useTheme } from "styled-components";
import { Search } from "lucide-react";
import DropdownMenu from "../components/dropdown/DropdownMenu";
import DropdownItem from "../components/dropdown/DropdownItem";
import Input from "../components/Input";
import { DefaultTheme } from "styled-components";
import { styles } from "../styles/themes/style";


interface IAdvancedSearchDropdownProps {
    placeholder?: string
    options?: any
    value?: any
    border?: string
    panelWidth?: string
    icon?: ReactNode
    /** field name shown in front of the selected value, e.g. "Industry: Food and Beverages" */
    label?: string
    onClick?: (e: any) => void
}

const AdvancedSearchDropdown: FunctionComponent<IAdvancedSearchDropdownProps> = ({placeholder, options, value, border, onClick, panelWidth, icon, label}) => {
    const [searchTerm, setSearchTerm] = useState<string>('')
    const theme: DefaultTheme = useTheme();

    const onClickHandler = (e: any) => {
        setSearchTerm('')
        onClick && onClick(e)
    }

    const valueLabel = (label ? value?.label?.replace?.(new RegExp(`^${label}:\\s*`), '') : value?.label) || value?.label
    const isActive = !!value?.value
    const filteredOptions = options?.filter((option: any) => option?.label?.toLowerCase().startsWith(searchTerm?.toLowerCase())) ?? []

    return (
        <StyledDropdownMenu
            title={
                <TriggerContent>
                    {icon && <IconSlot>{icon}</IconSlot>}
                    {label && <LabelText>{label}:</LabelText>}
                    <ValueText $active={isActive} $hasLabel={!!label}>{valueLabel}</ValueText>
                </TriggerContent>
            }
            selectStyle
            border={border}
            panelWidth={panelWidth}
        >
            <InputWrapper>
                <SearchIcon><Search size={14} strokeWidth={2} /></SearchIcon>
                <SearchInput placeholder={placeholder} prefilledValue={searchTerm} onChangeHandler={(e: any) => setSearchTerm(e)}/>
            </InputWrapper>
            <OptionsWrapper>
            {filteredOptions.length ? filteredOptions.map((option: any) => (
                        <StyledDropdownItem key={option?.value ?? option?.label} title={option?.label} $color={theme.primaryColor} onClickHandler={(e: any) => onClickHandler(option)}>{option?.label}</StyledDropdownItem>
                    )) : <EmptyState>No results found</EmptyState>}
            </OptionsWrapper>

        </StyledDropdownMenu>
    )

}
const TriggerContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    min-width: 0;
    min-height: 30px;
    flex: 1;
    overflow: hidden;
`
const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${styles.colors.black300};

    & svg {
        width: 16px;
        height: 16px;
    }
`
const LabelText = styled.span`
    flex-shrink: 0;
    font-weight: 500;
    color: ${styles.colors.black600};
    font-size: 14px;
`
const ValueText = styled.span<{ $active?: boolean, $hasLabel?: boolean }>`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    color: ${({ $active, $hasLabel }) => ($active && $hasLabel ? styles.colors.primary600 : $hasLabel ? styles.colors.black300 : styles.colors.black600)};
    font-size: 14px;
    font-weight: ${({ $active, $hasLabel }) => ($active && $hasLabel ? 600 : 400)};
`
const StyledDropdownMenu = styled(DropdownMenu)`
    border: ${({ border }) => border ? `${border}` : 'none'};
    .actions-dropdown + div {
        width: ${({ panelWidth }) => panelWidth ? `${panelWidth}` : '100%'};
        max-height: 350px;
        overflow: hidden !important;
        padding: 0;
    }
`
const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid ${styles.colors.black200};
`
const SearchIcon = styled.span`
    position: absolute;
    left: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    color: ${styles.colors.black300};
`
const SearchInput = styled(Input)`
    && {
        height: 34px;
        padding: 0 10px 0 30px;
        border: 1px solid ${styles.colors.black200};
        border-radius: 8px;
        font-size: 14px;
        font-weight: 400;

        &:hover { border: 1px solid ${styles.colors.black300}; }
        &:focus { border: 1px solid ${styles.colors.primary600}; }
    }
`
const OptionsWrapper = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 300px;
    padding: 6px 0;
`
const StyledDropdownItem = styled(DropdownItem)`
    && {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-weight: normal;
    }
`
const EmptyState = styled.div`
    padding: 10px 15px;
    font-size: 14px;
    color: ${styles.colors.black300};
`
export default AdvancedSearchDropdown
